<template>
  <div class="page-shell">
    <div class="page-glow page-glow--one"></div>
    <div class="page-glow page-glow--two"></div>

    <header class="topbar">
      <div class="topbar-inner">
        <div class="brand-block">
          <img class="brand-logo" :src="brandLogo" alt="太一排盘" />
          <div class="brand-copy">
            <div class="brand-name">太一排盘 · 奇门</div>
            <div class="brand-note">时家奇门 · 值符值使 · 九宫盘局速览</div>
          </div>
        </div>

        <nav class="station-nav" aria-label="太一站群">
          <template v-for="link in stationLinks" :key="link.key">
            <span v-if="link.current" class="station-link station-link--current">{{ link.label }}</span>
            <a
              v-else
              class="station-link"
              :href="link.href"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ link.label }}
            </a>
          </template>
        </nav>
      </div>
    </header>

    <main class="page-main">
      <div class="container">
        <div
          v-if="errorMsg"
          class="error-banner"
        >
          {{ errorMsg }}
        </div>

        <div class="header">
          <h1>{{ currentMode === 'time' ? '奇门遁甲排盘' : '奇门数字起局' }}</h1>
          <div class="sub-tabs" aria-label="奇门模式切换">
            <button
              type="button"
              :class="['sub-tab', { active: currentMode === 'time' }]"
              @click="switchMode('time')"
            >
              时家奇门
            </button>
            <button
              type="button"
              :class="['sub-tab', { active: currentMode === 'reported' }]"
              @click="switchMode('reported')"
            >
              奇门数字起局
            </button>
          </div>
          <div v-if="currentMode === 'time'" class="input-row">
            <label>排盘时间：</label>
            <input type="datetime-local" v-model="inputDateTime" @change="calculate" class="dt-input" />
            <button @click="useNow" class="btn-now">当前时辰</button>
          </div>
          <div v-else class="input-row">
            <label>报数：</label>
            <input
              v-model="reportedNumberInput"
              type="number"
              inputmode="numeric"
              min="1"
              step="1"
              class="dt-input dt-input--number"
              @change="calculate"
            />
            <button @click="calculate" class="btn-now">数字起局</button>
          </div>
          <div class="term-line" v-if="result">
            <span class="term-line-label">当月节气：</span>
            <span class="term-line-item">{{ result.prevTermName }}（{{ result.prevTermTime }}）</span>
            <span class="term-line-sep">·</span>
            <span class="term-line-item">{{ result.nextTermName }}（{{ result.nextTermTime }}）</span>
          </div>
          <div class="summary" v-if="result">
            <span class="tag">{{ result.juType }}</span>
            <span class="tag">{{ result.juName }}</span>
            <span v-if="result.mode === 'time'" class="tag">{{ result.season }}{{ result.yuan }}</span>
            <span v-else class="tag">报数定局</span>
            <span v-if="result.mode === 'reported'" class="tag">报数：{{ result.reportedNumber }} → {{ result.juNum }}局</span>
            <span class="tag">{{ result.yearGan }}{{ result.yearZhi }} | {{ result.monthGan }}{{ result.monthZhi }} | {{ result.dayGan }}{{ result.dayZhi }} | {{ result.hourGan }}{{ result.hourZhi }}</span>
          </div>
          <div class="summary summary--secondary" v-if="result && result.mode === 'reported'">
            <span class="summary-line">起局时刻：{{ result.usedDateTime }}</span>
          </div>
          <div class="summary summary--secondary" v-if="result">
            <span class="summary-line">日空亡：{{ result.dayKongWang }}</span>
            <span class="summary-line">时空亡：{{ result.hourKongWang }}</span>
          </div>
          <div class="summary summary--secondary" v-if="result">
            <span class="summary-line">值符：{{ result.zhiFuStar }}，落{{ result.zhiFuPalaceLabel }}</span>
            <span class="summary-line">值使：{{ result.zhiShiGate }}，落{{ result.zhiShiPalaceLabel }}</span>
            <span class="summary-line">旬首：{{ result.xunShou }}</span>
          </div>
        </div>

        <div class="grid-container" v-if="result">
          <div
            v-for="(cell, idx) in result.grid"
            :key="idx"
            class="grid-cell"
            :class="{ 'selected-cell': selectedPalace && selectedPalace.palace === cell.palace }"
            @click="selectPalace(cell)"
          >
            <span v-if="cell.isKong" class="kong-mark">空</span>
            
            <template v-if="!cell.isCenterPalace">
              <div class="row-text">
                <span :class="getShenColor(cell.shen)">{{ cell.shen }}</span>
                <span v-if="cell.isMa" class="text-blue ma-text">马</span>
              </div>

              <div class="row-text" :class="getGateColor(cell.gate)">
                {{ cell.gate }}
              </div>

              <div class="row-text">
                <span :class="getStarColor(cell.star)">{{ cell.star }}</span><span :class="getStemColor(cell.skyStem)">{{ cell.skyStem }}</span>
              </div>
            </template>
            <template v-else>
               <div class="row-text" style="height: 60px;"></div>
            </template>

            <div class="row-text text-black">
              <span>{{ cell.isCenterPalace ? '中五' : cell.direction.split(' ')[1] }}</span><span :class="getStemColor(cell.earthStem)">{{ cell.earthStem }}</span>
            </div>
          </div>
        </div>

        <section class="analysis-board" v-if="result">
          <div class="analysis-board-head">
            <h2 class="analysis-board-title">九宫解读总览</h2>
            <p class="analysis-board-note">九宫信息常驻显示，点击盘面或卡片可将对应宫位置顶并高亮，方便综合对照全盘。</p>
          </div>

          <div class="analysis-card-list">
            <article
              v-for="card in orderedAnalysisCards"
              :key="card.palace"
              :class="['analysis-card', { 'analysis-card--selected': card.isSelected }]"
              @click="selectPalace(card.rawCell)"
            >
              <div class="analysis-card-top">
                <div class="analysis-card-title">
                  <span class="analysis-card-palace">{{ card.title }}</span>
                  <span v-if="card.isSelected" class="analysis-card-current">当前选中</span>
                </div>
                <div v-if="card.flags.length" class="analysis-card-flags">
                  <span
                    v-for="flag in card.flags"
                    :key="flag"
                    :class="['analysis-chip', flag === '时空亡' ? 'analysis-chip--kong' : 'analysis-chip--ma']"
                  >
                    {{ flag }}
                  </span>
                </div>
              </div>

              <div class="analysis-card-grid">
                <div class="analysis-kv">
                  <span class="analysis-kv-label">神</span>
                  <span class="analysis-kv-value">{{ card.shen }}</span>
                </div>
                <div class="analysis-kv">
                  <span class="analysis-kv-label">门</span>
                  <span class="analysis-kv-value">{{ card.gate }}</span>
                </div>
                <div class="analysis-kv">
                  <span class="analysis-kv-label">星</span>
                  <span class="analysis-kv-value">{{ card.star }}</span>
                </div>
                <div class="analysis-kv">
                  <span class="analysis-kv-label">天盘干</span>
                  <span class="analysis-kv-value">{{ card.skyStem }}</span>
                </div>
                <div class="analysis-kv">
                  <span class="analysis-kv-label">地盘干</span>
                  <span class="analysis-kv-value">{{ card.earthStem }}</span>
                </div>
              </div>

              <div class="analysis-card-body">
                <template v-if="card.isCenterPalace">
                  <div class="analysis-item">
                    <strong>中五宫虚位</strong>
                    <div class="detail-row"><span>本盘中宫信息寄坤二宫。</span></div>
                    <div class="detail-row"><span>实际断局请参考坤二宫。</span></div>
                  </div>
                </template>
                <template v-else>
                  <div class="analysis-item" v-if="card.star !== '—'">
                    <strong>天盘星：{{ card.star }}</strong><span class="wx-tag">[{{ card.starWx }}]</span>
                    <div class="detail-row">
                      <span>月令状态：</span>
                      <span class="highlight">{{ card.starMonthState }}</span>
                    </div>
                  </div>
                  <div class="analysis-item" v-if="card.gate !== '—'">
                    <strong>人盘门：{{ card.gate }}</strong><span class="wx-tag">[{{ card.gateWx }}]</span>
                    <div class="detail-row">
                      <span>落宫状态：</span>
                      <span class="highlight">{{ card.gatePalaceState }}</span>
                    </div>
                    <div class="detail-row">
                      <span>月令状态：</span>
                      <span class="highlight">{{ card.gateMonthState }}</span>
                    </div>
                  </div>
                  <div class="analysis-item" v-if="card.skyStem !== '—'">
                    <strong>天盘奇仪：{{ card.skyStem }}</strong>
                    <div class="detail-row">
                      <span>落宫状态：</span>
                      <span class="highlight">{{ card.stemState }}</span>
                    </div>
                    <div class="detail-row" v-if="card.hasRuMu">
                      <span class="highlight analysis-warning">⚠️ 击刑 / 入墓 警示！此干在此宫受困严重！</span>
                    </div>
                  </div>
                </template>
              </div>
            </article>
          </div>
        </section>

        <div class="watermark">太一排盘-甲午子</div>
      </div>
    </main>

    <footer class="site-footer">
      <div class="site-footer-inner">
        <div class="footer-quote">万术归宗 · 太一奇门排盘</div>
        <div class="footer-meta">
          <div>Email: <a :href="`mailto:${site.supportEmail}`">{{ site.supportEmail }}</a></div>
          <div v-if="site.isCn" class="site-records">
            <a :href="site.records.icpUrl" target="_blank" rel="noopener noreferrer">{{ site.records.icp }}</a>
            <a :href="site.records.publicUrl" target="_blank" rel="noopener noreferrer">{{ site.records.public }}</a>
          </div>
          <div>&copy; {{ currentYear }} {{ site.hostLabel }} · 弘扬传统文化</div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
// 引入 tyme4ts 及其相关核心类
import { SolarTime } from 'tyme4ts';
import { siteConfig } from './config/site';

/* eslint-disable no-unused-vars */

const TG = ['甲','乙','丙','丁','戊','己','庚','辛','壬','癸'];
const DZ = ['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'];

const PALACES = [
  { palace: 4, direction: '东南 巽四' },
  { palace: 9, direction: '正南 离九' },
  { palace: 2, direction: '西南 坤二' },
  { palace: 3, direction: '正东 震三' },
  { palace: 5, direction: '中宫' },
  { palace: 7, direction: '正西 兑七' },
  { palace: 8, direction: '东北 艮八' },
  { palace: 1, direction: '正北 坎一' },
  { palace: 6, direction: '西北 乾六' },
];

const NINE_STARS = {
  1: { name: '天蓬', type: 'water' }, 2: { name: '天芮', type: 'earth' },
  3: { name: '天冲', type: 'wood' },  4: { name: '天辅', type: 'wood' },
  5: { name: '天禽', type: 'earth' }, 6: { name: '天心', type: 'metal' },
  7: { name: '天柱', type: 'metal' }, 8: { name: '天任', type: 'earth' },
  9: { name: '天英', type: 'fire' }
};

const EIGHT_GATES = {
  1: { name: '休门', type: 'lucky' },  2: { name: '死门', type: 'ominous' },
  3: { name: '伤门', type: 'warn' },   4: { name: '杜门', type: 'warn' },
  5: { name: '——',   type: 'neutral' }, 6: { name: '开门', type: 'lucky' },
  7: { name: '惊门', type: 'warn' },   8: { name: '生门', type: 'lucky' },
  9: { name: '景门', type: 'warn' }
};

const EIGHT_SHEN = ['值符','腾蛇','太阴','六合','白虎','玄武','九地','九天'];

const EARTH_STEMS = {
  1: { 1:'癸', 2:'乙', 3:'丙', 4:'辛', 5:'己', 6:'庚', 7:'丁', 8:'戊', 9:'壬' },
  2: { 1:'壬', 2:'癸', 3:'乙', 4:'庚', 5:'己', 6:'辛', 7:'丙', 8:'丁', 9:'戊' },
  3: { 1:'丁', 2:'壬', 3:'癸', 4:'乙', 5:'己', 6:'庚', 7:'辛', 8:'丙', 9:'戊' },
  4: { 1:'丙', 2:'丁', 3:'壬', 4:'癸', 5:'己', 6:'乙', 7:'庚', 8:'辛', 9:'戊' },
  5: { 1:'辛', 2:'丙', 3:'丁', 4:'壬', 5:'己', 6:'癸', 7:'乙', 8:'庚', 9:'戊' },
  6: { 1:'庚', 2:'辛', 3:'丙', 4:'丁', 5:'己', 6:'壬', 7:'癸', 8:'乙', 9:'戊' },
  7: { 1:'乙', 2:'庚', 3:'辛', 4:'丙', 5:'己', 6:'丁', 7:'壬', 8:'癸', 9:'戊' },
  8: { 1:'癸', 2:'乙', 3:'庚', 4:'辛', 5:'己', 6:'丙', 7:'丁', 8:'壬', 9:'戊' },
  9: { 1:'壬', 2:'癸', 3:'乙', 4:'庚', 5:'己', 6:'辛', 7:'丙', 8:'丁', 9:'戊' },
};

function formatSolarTimeDisplay(solarTime) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${pad(solarTime.getMonth())}-${pad(solarTime.getDay())} ${pad(solarTime.getHour())}:${pad(solarTime.getMinute())}`;
}

function formatDateTimeDisplay(date) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatPalaceLabel(palaceNum) {
  const palace = PALACES.find((item) => item.palace === palaceNum);

  if (!palace) {
    return '';
  }

  return palace.palace === 5 ? '中宫' : `${palace.direction.split(' ')[1]}宫`;
}

// ================================================
// 基于 tyme4ts 的四柱干支与历法计算
// ================================================
function getGanZhi(date) {
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();
  const h = date.getHours();
  const min = date.getMinutes();
  const s = date.getSeconds();

  const solarTime = SolarTime.fromYmdHms(y, m, d, h, min, s);
  
  // 八字
  const lunarHour = solarTime.getLunarHour();
  const eightChar = lunarHour.getEightChar();
  const yearPillar = eightChar.getYear();
  const monthPillar = eightChar.getMonth();
  const dayPillar = eightChar.getDay();
  const hourPillar = eightChar.getHour();

  // 日柱旬空
  const kwDayBranches = dayPillar.getExtraEarthBranches();
  
  // 时柱旬空和旬首（时家奇门排盘唯一基准）
  const kwHourBranches = hourPillar.getExtraEarthBranches();
  const dayKongWang = kwDayBranches[0].getName() + kwDayBranches[1].getName();
  const hourKongWang = kwHourBranches[0].getName() + kwHourBranches[1].getName();
  const xunStr = hourPillar.getTen().getName();
  const prevTerm = solarTime.getTerm();
  const nextTerm = prevTerm.next(1);
  const prevTermTime = prevTerm.getJulianDay().getSolarTime();
  const nextTermTime = nextTerm.getJulianDay().getSolarTime();

  // 当时所在的节气
  const termName = prevTerm.getName();
  
  const T24_NAMES = [
    '冬至', '小寒', '大寒', '立春', '雨水', '惊蛰', '春分', '清明', '谷雨', '立夏', '小满', '芒种',
    '夏至', '小暑', '大暑', '立秋', '处暑', '白露', '秋分', '寒露', '霜降', '立冬', '小雪', '大雪'
  ];
  let termIdx = T24_NAMES.indexOf(termName);

  return {
    yearGan: yearPillar.getHeavenStem().getName(),
    yearZhi: yearPillar.getEarthBranch().getName(),
    monthGan: monthPillar.getHeavenStem().getName(),
    monthZhi: monthPillar.getEarthBranch().getName(),
    dayGan: dayPillar.getHeavenStem().getName(),
    dayZhi: dayPillar.getEarthBranch().getName(),
    hourGan: hourPillar.getHeavenStem().getName(),
    hourZhi: hourPillar.getEarthBranch().getName(),
    xunShou: xunStr + '旬',
    dayKongWang,
    hourKongWang,
    prevTermName: prevTerm.getName(),
    prevTermTime: formatSolarTimeDisplay(prevTermTime),
    nextTermName: nextTerm.getName(),
    nextTermTime: formatSolarTimeDisplay(nextTermTime),
    termIdx,
    termName: termName
  };
}

// 【时家奇门 - 拆补法定局】
// 24节气对应的每气三元局数表 (上元, 中元, 下元)
const JU_MAP = {
  // 阳遁 12气 (冬至 -> 芒种)
  '冬至': [1, 7, 4], '小寒': [2, 8, 5], '大寒': [3, 9, 6],
  '立春': [8, 5, 2], '雨水': [9, 6, 3], '惊蛰': [1, 7, 4],
  '春分': [3, 9, 6], '清明': [4, 1, 7], '谷雨': [5, 2, 8],
  '立夏': [4, 1, 7], '小满': [5, 2, 8], '芒种': [6, 3, 9],
  // 阴遁 12气 (夏至 -> 大雪)
  '夏至': [9, 3, 6], '小暑': [8, 2, 5], '大暑': [7, 1, 4],
  '立秋': [2, 5, 8], '处暑': [1, 4, 7], '白露': [9, 3, 6],
  '秋分': [7, 1, 4], '寒露': [6, 9, 3], '霜降': [5, 8, 2],
  '立冬': [6, 9, 3], '小雪': [5, 8, 2], '大雪': [4, 7, 1]
};

function calcJu(gz) {
  // 1. 判断阴阳遁 (根据节气名称)
  const yangDunTerms = ['冬至','小寒','大寒','立春','雨水','惊蛰','春分','清明','谷雨','立夏','小满','芒种'];
  const isYang = yangDunTerms.includes(gz.termName);

  // 2. 找符头 (六十甲子中，每5天为一个符头，符头干必定是 甲 或 己)
  // 干支组合总共60个，我们可以通过天干地支序号来算出距离最近的甲或己的步长
  const tgIdx = TG.indexOf(gz.dayGan); // 0是甲, 1是乙...
  // 往前推多少天能到甲或己？tgIdx%5就是距离符头的天数
  const stepsToFutou = tgIdx % 5;
  
  // 推算出符头的地支索引
  const dzIdx = DZ.indexOf(gz.dayZhi);
  const futouDzIdx = (dzIdx - stepsToFutou + 12) % 12; // 符头地支索引
  
  // 3. 判断上中下元
  // 子午卯酉(0,6,3,9) -> 上元(0)
  // 寅申巳亥(2,8,5,11) -> 中元(1)
  // 辰戌丑未(4,10,1,7) -> 下元(2)
  let yuanIdx = 0; // 0上元, 1中元, 2下元
  if ([0, 6, 3, 9].includes(futouDzIdx)) yuanIdx = 0;
  else if ([2, 8, 5, 11].includes(futouDzIdx)) yuanIdx = 1;
  else if ([4, 10, 1, 7].includes(futouDzIdx)) yuanIdx = 2;

  // 4. 定局数
  let juNum = 1;
  const termJuArray = JU_MAP[gz.termName];
  if (termJuArray && termJuArray.length === 3) {
    juNum = termJuArray[yuanIdx];
  } else {
    // 降级保护：如果找不到节气或表格出错，默认抛出1局
    juNum = 1;
  }

  const yuanNames = ['上元', '中元', '下元'];

  return {
    isYang,
    juNum,
    yuan: yuanNames[yuanIdx],
    juType: isYang ? '阳遁' : '阴遁',
    juName: `${isYang ? '阳' : '阴'}遁${juNum}局`,
    season: gz.termName
  };
}

function calcReportedJu(gz, reportedNumber) {
  const yangDunTerms = ['冬至','小寒','大寒','立春','雨水','惊蛰','春分','清明','谷雨','立夏','小满','芒种'];
  const isYang = yangDunTerms.includes(gz.termName);
  let juNum = reportedNumber % 9;

  if (juNum === 0) {
    juNum = 9;
  }

  return {
    isYang,
    juNum,
    yuan: '',
    juType: isYang ? '阳遁' : '阴遁',
    juName: `${isYang ? '阳' : '阴'}遁${juNum}局`,
    season: gz.termName,
    juSource: 'reported'
  };
}

// ======= 排盘基础常数 =======
const JIA_YI = { '甲子': '戊', '甲戌': '己', '甲申': '庚', '甲午': '辛', '甲辰': '壬', '甲寅': '癸' };
const SHEN_ORDER_YANG = ['值符', '腾蛇', '太阴', '六合', '白虎', '玄武', '九地', '九天'];
const SHEN_ORDER_YIN  = ['值符', '九天', '九地', '玄武', '白虎', '六合', '太阴', '腾蛇'];
const RING_PALACES = [1, 8, 3, 4, 9, 2, 7, 6]; // 坎艮震巽离坤兑乾
const INSTRUMENTS = ['戊', '己', '庚', '辛', '壬', '癸', '丁', '丙', '乙'];

const ORIGINAL_STARS = { 1: '天蓬', 2: '天芮', 3: '天冲', 4: '天辅', 5: '天禽', 6: '天心', 7: '天柱', 8: '天任', 9: '天英' };
const ORIGINAL_GATES = { 1: '休门', 2: '死门', 3: '伤门', 4: '杜门', 5: '死门', 6: '开门', 7: '惊门', 8: '生门', 9: '景门' };

function getStarType(star) {
  if (star.includes('蓬')) return 'water';
  if (star.includes('芮') || star.includes('任') || star.includes('禽')) return 'earth';
  if (star.includes('冲') || star.includes('辅')) return 'wood';
  if (star.includes('英')) return 'fire';
  if (star.includes('心') || star.includes('柱')) return 'metal';
  return 'neutral';
}

function getGateType(gate) {
  if (['休门','生门','开门'].includes(gate)) return 'lucky';
  if (['死门','惊门'].includes(gate)) return 'ominous';
  if (['伤门','杜门','景门'].includes(gate)) return 'warn';
  return 'neutral';
}

// ================================================
// 核心布盘：转盘法推演 (飞符寻使，星门连珠)
// ================================================
function buildGrid(gz, ju) {
  const { juNum, isYang } = ju;
  
  // 1. 排地盘三奇六仪
  const diPanStems = {}; // 宫位 -> 天干
  const stemPan = {};    // 天干 -> 宫位
  for (let i = 0; i < 9; i++) {
    let p = isYang ? (juNum + i) : (juNum - i);
    p = ((p - 1) % 9 + 9) % 9 + 1;
    diPanStems[p] = INSTRUMENTS[i];
    stemPan[INSTRUMENTS[i]] = p;
  }
  
  // 提取旬首干仪 (如甲子->戊)
  const xunStr = gz.xunShou.replace('旬', '');
  const futouStem = JIA_YI[xunStr];
  
  // 旬首落地盘哪宫？ (中宫寄居坤二宫)
  let xunPalace = stemPan[futouStem];
  if (xunPalace === 5) xunPalace = 2;
  
  // 2. 确定值符 (星) 和值使 (门)
  const zhiFuStar = ORIGINAL_STARS[xunPalace];
  const zhiShiGate = ORIGINAL_GATES[xunPalace];
  
  // 3. 值符落宫 -> 推演天盘星与天盘奇仪
  let hourStem = gz.hourGan;
  if (hourStem === '甲') {
    hourStem = futouStem; // 甲时就用本旬首的仪
  }
  let tianPanTargetPalace = stemPan[hourStem];
  if (tianPanTargetPalace === 5) tianPanTargetPalace = 2; // 中宫寄坤二宫
  
  const ringIdxOrig = RING_PALACES.indexOf(xunPalace);
  const ringIdxTarget = RING_PALACES.indexOf(tianPanTargetPalace);
  const starOffset = (ringIdxTarget - ringIdxOrig + 8) % 8;
  
  const tianPanStars = {};
  const tianPanStems = {};
  for (let i = 0; i < 8; i++) {
    const origP = RING_PALACES[i];
    const starVal = ORIGINAL_STARS[origP];
    // 地盘原星下的奇仪也随天盘飞
    const stemVal = diPanStems[origP];
    
    // 飞伏的落点
    const targetP = RING_PALACES[(i + starOffset) % 8];
    tianPanStars[targetP] = starVal;
    tianPanStems[targetP] = stemVal;
    
    // 如果是天芮星，带上天禽星及中宫的地盘干同飞
    if (origP === 2) {
      const qinStem = diPanStems[5] ? diPanStems[5] : '';
      tianPanStars[targetP] = qinStem + '禽芮'; 
      tianPanStems[targetP] = stemVal;
    }
  }
  
  // 4. 值使落宫 -> 推演人盘八门
  // 从旬首时辰查到当前时辰经历了多少步
  const xunZhi = xunStr[1];
  const hourZhiIdx = DZ.indexOf(gz.hourZhi);
  const xunZhiIdx = DZ.indexOf(xunZhi);
  const steps = (hourZhiIdx - xunZhiIdx + 12) % 12; 
  
  // 门在九宫上阳顺阴逆走宫位 (1-9)
  let zhiShiTargetPalace = isYang ? (xunPalace + steps) : (xunPalace - steps);
  zhiShiTargetPalace = ((zhiShiTargetPalace - 1) % 9 + 9) % 9 + 1;
  if (zhiShiTargetPalace === 5) zhiShiTargetPalace = 2;
  
  const gateOrigRingIdx = RING_PALACES.indexOf(xunPalace);
  const gateTargetRingIdx = RING_PALACES.indexOf(zhiShiTargetPalace);
  const gateOffset = (gateTargetRingIdx - gateOrigRingIdx + 8) % 8;
  
  // 八门按 1,8,3,4,9,2,7,6 环形连珠转动原始序
  const gateRingValues = ['休门', '生门', '伤门', '杜门', '景门', '死门', '惊门', '开门'];
  const renPanGates = {};
  for(let i=0; i < 8; i++) {
    const targetP = RING_PALACES[(i + gateOffset) % 8];
    renPanGates[targetP] = gateRingValues[i];
  }
  
  // 5. 神盘落宫 -> 提取小值符跟随大值符（星）
  const zhiFuGodPalace = tianPanTargetPalace;
  const godRingStartIdx = RING_PALACES.indexOf(zhiFuGodPalace);
  const shenPan = {};
  const shenArray = isYang ? SHEN_ORDER_YANG : SHEN_ORDER_YIN;
  for(let i=0; i < 8; i++) {
    const targetP = RING_PALACES[(godRingStartIdx + i) % 8];
    shenPan[targetP] = shenArray[i];
  }
  
  // ==========================
  // 6. 追加神杀：马星与空亡归属
  // ==========================
  // 马星：申子辰马在寅(8), 寅午戌马在申(2), 亥卯未马在巳(4), 巳酉丑马在亥(6)
  const maMap = {
    '申': 8, '子': 8, '辰': 8,
    '寅': 2, '午': 2, '戌': 2,
    '亥': 4, '卯': 4, '未': 4,
    '巳': 6, '酉': 6, '丑': 6
  };
  const maPalace = maMap[gz.hourZhi] || 0;

  // 将旬空的长字符串切分，映射为空亡宫位寻找（简易实现：找到该地支对应的本宫）
  // 亥子->坎1, 寅卯->震3/艮8, 巳午->离9/巽4, 申酉->兑7/坤2, 辰戌丑未->四维宫
  const kwBranches = gz.hourKongWang ? gz.hourKongWang.split('') : [];
  const zhiPalaceMap = {
    '子': 1, '丑': 8, '寅': 8, '卯': 3, '辰': 4, '巳': 4,
    '午': 9, '未': 2, '申': 2, '酉': 7, '戌': 6, '亥': 6
  };
  const kongPalaces = kwBranches.map(z => zhiPalaceMap[z]).filter(p => p !== undefined);

  // 判断空亡的合并关系 (例如 7和6空亡)
  const isKongPalace = (p) => kongPalaces.includes(p);

  // 组合赋值给 Vue UI 供渲染
  const cells = PALACES.map(p => {
    const pal = p.palace;
    let star = tianPanStars[pal];
    let stem = tianPanStems[pal];
    
    // 中宫天盘无落星（被拉去了其它宫）
    if (pal === 5) {
      star = '天禽';
      stem = diPanStems[5];
    }
    
    const uiGate = renPanGates[pal] || '—';
    const uiShen = shenPan[pal] || '';

    return {
      ...p,
      isCenterPalace: pal === 5,
      earthStem: diPanStems[pal], // 地盘仪
      skyStem: stem || '—',       // 天盘仪
      star: star || '—',
      gate: pal === 5 ? '—' : uiGate,
      shen: uiShen,
      // 附加标记
      isMa: pal === maPalace,
      isKong: isKongPalace(pal)
    };
  });

  return {
    cells,
    zhiFuStar,
    zhiShiGate,
    zhiFuPalace: zhiFuGodPalace,
    zhiShiPalace: zhiShiTargetPalace
  };
}

export default {
  name: 'App',
  data() {
    return {
      brandLogo: `${process.env.BASE_URL}logo.png`,
      currentYear: new Date().getFullYear(),
      site: siteConfig,
      stationLinks: siteConfig.stationLinks,
      currentMode: 'time',
      inputDateTime: this.nowLocal(),
      reportedNumberInput: '',
      result: null,
      errorMsg: null,
      selectedPalace: null
    }
  },
  computed: {
    orderedAnalysisCards() {
      if (!this.result) {
        return [];
      }

      const cards = this.result.grid.map((cell) => this.buildAnalysisCard(cell));
      const selectedPalaceNum = this.selectedPalace?.palace;

      if (!selectedPalaceNum) {
        return cards;
      }

      const selectedCard = cards.find((card) => card.palace === selectedPalaceNum);
      const restCards = cards.filter((card) => card.palace !== selectedPalaceNum);

      return selectedCard ? [selectedCard, ...restCards] : cards;
    }
  },
  mounted() {
    this.syncPageTitle();
    this.calculate();
  },
  methods: {
    syncPageTitle() {
      document.title = this.currentMode === 'reported'
        ? '奇门数字起局 - 太一排盘'
        : '太一奇门排盘 - 专注奇门遁甲时家排盘的纯净在线排盘系统';
    },
    switchMode(mode) {
      if (this.currentMode === mode) {
        return;
      }

      this.currentMode = mode;
      this.errorMsg = null;
      this.result = null;
      this.selectedPalace = null;
      this.syncPageTitle();

      if (mode === 'time') {
        this.calculate();
      } else if (this.reportedNumberInput) {
        this.calculate();
      }
    },
    selectPalace(cell) {
      this.selectedPalace = cell;
    },
    buildAnalysisCard(cell) {
      const monthWx = this.getWuXingZhi(this.result.monthZhi);
      const starWx = cell.star !== '—' ? this.getWuXingStar(cell.star) : '';
      const gateWx = cell.gate !== '—' ? this.getWuXingGate(cell.gate) : '';
      const stemState = cell.skyStem !== '—' ? this.getShiErChangSheng(cell.skyStem, cell.palace) : '';
      const flags = [];

      if (cell.isKong) {
        flags.push('时空亡');
      }

      if (cell.isMa) {
        flags.push('马星');
      }

      return {
        rawCell: cell,
        palace: cell.palace,
        title: cell.isCenterPalace ? '中宫' : cell.direction,
        isCenterPalace: cell.isCenterPalace,
        isSelected: this.selectedPalace?.palace === cell.palace,
        shen: cell.shen || '—',
        gate: cell.gate || '—',
        star: cell.star || '—',
        skyStem: cell.skyStem || '—',
        earthStem: cell.earthStem || '—',
        flags,
        starWx,
        starMonthState: starWx ? this.getStarWangShuai(starWx, monthWx) : '',
        gateWx,
        gatePalaceState: gateWx ? this.getGateWangShuaiPalace(gateWx, this.getWuXingPalace(cell.palace)) : '',
        gateMonthState: gateWx ? this.getGateWangShuaiMonth(gateWx, monthWx) : '',
        stemState,
        hasRuMu: cell.skyStem !== '—' && this.checkRuMu(cell.skyStem, cell.palace)
      };
    },
    getShenColor(shen) {
      if (['值符', '太阴', '六合', '九天', '九地'].includes(shen)) return 'text-red';
      return 'text-black';
    },
    getGateColor(gate) {
      if (['开门', '休门', '生门', '景门'].includes(gate)) return 'text-red';
      return 'text-black';
    },
    getStarColor(star) {
      if (['天心', '天禽', '天辅', '天任', '天冲'].some((name) => star.includes(name))) return 'text-red';
      return 'text-black';
    },
    getStemColor(stem) {
      if (!stem || stem === '—' || !this.result) return 'text-black';
      if ([this.result.dayGan, this.result.hourGan].includes(stem)) return 'text-blue';
      return 'text-black';
    },
    getWuXingZhi(zhi) {
      if (['亥','子'].includes(zhi)) return '水';
      if (['寅','卯'].includes(zhi)) return '木';
      if (['巳','午'].includes(zhi)) return '火';
      if (['申','酉'].includes(zhi)) return '金';
      if (['辰','戌','丑','未'].includes(zhi)) return '土';
      return '土';
    },
    getWuXingStar(star) {
      if (star.includes('蓬')) return '水';
      if (star.includes('芮')||star.includes('禽')||star.includes('任')) return '土';
      if (star.includes('冲')||star.includes('辅')) return '木';
      if (star.includes('英')) return '火';
      if (star.includes('心')||star.includes('柱')) return '金';
      return '';
    },
    getWuXingGate(gate) {
      if (gate.includes('休')) return '水';
      if (gate.includes('死')||gate.includes('生')) return '土';
      if (gate.includes('伤')||gate.includes('杜')) return '木';
      if (gate.includes('景')) return '火';
      if (gate.includes('惊')||gate.includes('开')) return '金';
      return '';
    },
    getWuXingPalace(palaceNum) {
      const m = {1:'水', 2:'土', 3:'木', 4:'木', 5:'土', 6:'金', 7:'金', 8:'土', 9:'火'};
      return m[palaceNum] || '土';
    },
    getStarWangShuai(starWx, monthWx) {
      if (!starWx || !monthWx) return '';
      const sheng = { '木':'火', '火':'土', '土':'金', '金':'水', '水':'木' };
      const ke = { '木':'土', '土':'水', '水':'火', '火':'金', '金':'木' };
      if (sheng[starWx] === monthWx) return '旺 (我生之月)';
      if (starWx === monthWx) return '相 (与我同行)';
      if (sheng[monthWx] === starWx) return '废 (生我之月)';
      if (ke[starWx] === monthWx) return '休 (我克之月)';
      if (ke[monthWx] === starWx) return '囚 (克我之月)';
      return '';
    },
    getGateWangShuaiPalace(gateWx, palWx) {
      if (!gateWx || !palWx) return '';
      const sheng = { '木':'火', '火':'土', '土':'金', '金':'水', '水':'木' };
      const ke = { '木':'土', '土':'水', '水':'火', '火':'金', '金':'木' };
      if (gateWx === palWx) return '比和 (吉)';
      if (sheng[gateWx] === palWx) return '相 (门生宫/吉)';
      if (sheng[palWx] === gateWx) return '义 (宫生门/吉)';
      if (ke[gateWx] === palWx) return '迫 (门克宫/凶)';
      if (ke[palWx] === gateWx) return '制 (宫克门/凶)';
      return '';
    },
    getGateWangShuaiMonth(gateWx, monthWx) {
      if (!gateWx || !monthWx) return '';
      const sheng = { '木':'火', '火':'土', '土':'金', '金':'水', '水':'木' };
      const ke = { '木':'土', '土':'水', '水':'火', '火':'金', '金':'木' };
      if (gateWx === monthWx) return '旺 (当令)';
      if (sheng[monthWx] === gateWx) return '相 (月生门)';
      if (sheng[gateWx] === monthWx) return '休 (门生月)';
      if (ke[gateWx] === monthWx) return '囚 (门克月)';
      if (ke[monthWx] === gateWx) return '死 (月克门)';
      return '';
    },
    getShiErChangSheng(stem, palaceNum) {
      if (!stem || stem === '—') return '';
      // 奇门中，甲寄于寅(8宫), 其实一般看甲直接看它遁的六仪戊己庚辛壬癸即可
      // 此处主要为乙丙丁三奇和戊己庚辛壬癸六仪计算（阳顺阴逆：木生亥，火戊生寅，金生巳，水生申）
      // 这里的逻辑使用传统奇门的十二长生表推断，返回其在1-9宫的具体阶段
      const csMap = {
        '甲': {8:'临官',3:'帝旺',4:'衰、病',9:'死',2:'墓、绝',7:'胎、养',6:'养、生',1:'沐浴'}, 
        '乙': {8:'帝、衰',3:'临官',4:'病、死',9:'长生',2:'墓、绝',7:'胎、养',6:'死、墓',1:'病'}, // 这里的乙和上文的稍微重构，奇门实际推断乙木长生在午(9宫)
        '丙': {8:'养、生',3:'沐浴',4:'冠、临',9:'帝旺',2:'衰、病',7:'死',6:'墓、绝',1:'胎'}, // 丙火戊土，长生在寅(8宫，寅内含丑寅)
        '戊': {8:'养、生',3:'沐浴',4:'冠、临',9:'帝旺',2:'衰、病',7:'死',6:'墓、绝',1:'胎'},
        '丁': {8:'墓、绝',3:'病',4:'帝、衰',9:'临官',2:'冠、沐',7:'长生',6:'胎、养',1:'绝'}, // 丁阴火己土，逆行，长生在酉(7宫)
        '己': {8:'墓、绝',3:'病',4:'帝、衰',9:'临官',2:'冠、沐',7:'长生',6:'胎、养',1:'绝'}, 
        '庚': {8:'墓、绝',3:'胎',4:'养、生',9:'沐浴',2:'冠、临',7:'帝旺',6:'衰、病',1:'死'}, // 庚阳金，长生在巳(4宫)
        '辛': {8:'胎、养',3:'绝',4:'墓、绝',9:'死',2:'衰、病',7:'临官',6:'帝、衰',1:'长生'}, // 辛阴金，长生在子(1宫)
        '壬': {8:'衰、病',3:'死',4:'墓、绝',9:'胎',2:'养、生',7:'沐浴',6:'冠、临',1:'帝旺'}, // 壬阳水，长生在申(2宫)
        '癸': {8:'冠、沐',3:'长生',4:'胎、养',9:'绝',2:'墓、绝',7:'病',6:'帝、衰',1:'临官'}, // 癸阴水，长生在卯(3宫)
      };
      
      const res = csMap[stem] && csMap[stem][palaceNum];
      return res ? `${res}` : '中宫无长生'; 
    },
    checkRuMu(stem, palaceNum) {
      if (!stem || stem === '—') return false;
      // 甲乙入坤二宫墓，丙丁戊己入乾六宫墓，庚辛入艮八宫墓，壬癸入巽四宫墓
      if (['甲','乙'].includes(stem) && palaceNum === 2) return true;
      if (['丙','丁','戊','己'].includes(stem) && palaceNum === 6) return true;
      if (['庚','辛'].includes(stem) && palaceNum === 8) return true;
      if (['壬','癸'].includes(stem) && palaceNum === 4) return true;
      return false;
    },
    nowLocal() {
      const n = new Date();
      const p = x => String(x).padStart(2, '0');
      return `${n.getFullYear()}-${p(n.getMonth()+1)}-${p(n.getDate())}T${p(n.getHours())}:${p(n.getMinutes())}`;
    },
    buildUserFacingError(error) {
      const message = typeof error?.message === 'string' ? error.message : '';

      if (
        message === 'INVALID_INPUT_DATETIME' ||
        message.includes('illegal solar year') ||
        message.includes('Invalid Date')
      ) {
        return '排盘失败：请输入有效的日期与时间。';
      }

      if (message === 'INVALID_REPORTED_NUMBER') {
        return '起局失败：请输入有效的正整数。';
      }

      return '排盘失败：当前时间参数无法完成排盘，请调整后重试。';
    },
    useNow() { this.inputDateTime = this.nowLocal(); this.calculate(); },
    createResult(date, ju, extra = {}) {
      const gz = getGanZhi(date);
      const finalJu = typeof ju === 'function' ? ju(gz) : ju;
      const gridData = buildGrid(gz, finalJu);

      this.result = {
        ...gz,
        ...finalJu,
        ...extra,
        grid: gridData.cells,
        zhiFuStar: gridData.zhiFuStar,
        zhiFuPalaceLabel: formatPalaceLabel(gridData.zhiFuPalace),
        zhiShiGate: gridData.zhiShiGate,
        zhiShiPalaceLabel: formatPalaceLabel(gridData.zhiShiPalace)
      };
    },
    calculate() {
      try {
        this.errorMsg = null;
        this.selectedPalace = null;
        if (this.currentMode === 'reported') {
          const reportedNumber = Number.parseInt(String(this.reportedNumberInput).trim(), 10);

          if (!Number.isFinite(reportedNumber) || reportedNumber <= 0) {
            throw new Error('INVALID_REPORTED_NUMBER');
          }

          const date = new Date();
          this.createResult(date, (gz) => calcReportedJu(gz, reportedNumber), {
            mode: 'reported',
            reportedNumber,
            usedDateTime: formatDateTimeDisplay(date)
          });
        } else {
          const date = new Date(this.inputDateTime);

          if (!this.inputDateTime || Number.isNaN(date.getTime())) {
            throw new Error('INVALID_INPUT_DATETIME');
          }

          this.createResult(date, calcJu, {
            mode: 'time'
          });
        }
        this.selectedPalace = null;
      } catch (e) {
        this.result = null;
        this.errorMsg = this.buildUserFacingError(e);
        console.error('计算错误:', e);
      }
    }
  }
};
</script>

<style>
* { box-sizing: border-box; }
body {
  font-family:
    "PingFang SC",
    "Microsoft YaHei",
    "Noto Sans SC",
    "Helvetica Neue",
    Arial,
    sans-serif;
  background:
    radial-gradient(circle at top left, rgba(157, 123, 240, 0.18), transparent 28%),
    radial-gradient(circle at 84% 10%, rgba(122, 73, 226, 0.1), transparent 22%),
    linear-gradient(180deg, #fbf9ff 0%, #f2eef9 46%, #f6f2fd 100%);
  margin: 0;
  color: #312f63;
}
a {
  color: inherit;
  text-decoration: none;
}
.page-shell {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
}
.page-glow {
  position: fixed;
  border-radius: 999px;
  pointer-events: none;
  filter: blur(8px);
  opacity: 0.7;
}
.page-glow--one {
  top: -120px;
  left: -80px;
  width: 320px;
  height: 320px;
  background: rgba(157, 123, 240, 0.18);
}
.page-glow--two {
  top: 180px;
  right: -100px;
  width: 280px;
  height: 280px;
  background: rgba(122, 73, 226, 0.1);
}
.topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  padding: 14px 20px 0;
}
.topbar-inner {
  max-width: 1240px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
}
.brand-block {
  display: flex;
  align-items: center;
  gap: 12px;
}
.brand-logo {
  width: 54px;
  height: 54px;
  object-fit: contain;
  display: block;
  flex: 0 0 auto;
}
.brand-name {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}
.brand-note {
  margin-top: 3px;
  font-size: 13px;
  color: #707499;
}
.station-nav {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}
.station-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 8px;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
  color: #707499;
  transition: color 0.2s ease, opacity 0.2s ease;
}
.station-link:hover {
  color: #7a49e2;
  opacity: 0.9;
}
.station-link--current {
  color: #7a49e2;
}
.page-main {
  max-width: 1240px;
  margin: 0 auto;
  padding: 14px 20px 34px;
}
.container {
  max-width: 760px;
  margin: 0 auto;
  padding: 18px 20px 0;
}
.error-banner {
  background: rgba(255, 204, 204, 0.92);
  color: #b42318;
  padding: 18px;
  white-space: pre-wrap;
  font-size: 14px;
  margin-bottom: 20px;
  border-radius: 16px;
  border: 1px solid rgba(180, 35, 24, 0.18);
}
.header { text-align: center; margin-bottom: 20px; }
h1 {
  color: #312f63;
  margin-bottom: 15px;
  font-size: 1.9rem;
  letter-spacing: 4px;
  font-family:
    "Songti SC",
    "STSong",
    "Noto Serif SC",
    "Source Han Serif SC",
    serif;
}
.sub-tabs {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  padding: 6px;
  margin-bottom: 14px;
  border-radius: 18px;
  background: rgba(122, 73, 226, 0.08);
}
.sub-tab {
  min-height: 40px;
  padding: 0 14px;
  border: none;
  border-radius: 14px;
  background: transparent;
  color: #707499;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}
.sub-tab:hover {
  color: #312f63;
  background: rgba(255, 255, 255, 0.54);
}
.sub-tab.active {
  background: #fff;
  color: #7a49e2;
  box-shadow: 0 8px 16px rgba(122, 73, 226, 0.12);
}
.input-row { display: flex; gap: 8px; align-items: center; justify-content: center; flex-wrap: wrap; }
.dt-input { font-size: 0.95rem; padding: 5px 8px; border: 1px solid #ccc; border-radius: 6px; }
.dt-input--number {
  width: 120px;
  text-align: center;
}
.btn-now { background: #7a49e2; color: #fff; border: none; padding: 6px 14px; border-radius: 10px; cursor: pointer; }
.btn-now:hover { background: #6538c8; }
.term-line {
  margin-top: 10px;
  font-size: 14px;
  line-height: 1.75;
  color: #707499;
}
.term-line-label {
  color: #4e4b7d;
  font-weight: 600;
}
.term-line-item {
  color: #5d5890;
}
.term-line-sep {
  margin: 0 8px;
  color: #9b97bd;
}
.summary { margin-top: 12px; display: flex; flex-wrap: wrap; gap: 6px; justify-content: center; }
.summary--secondary {
  margin-top: 10px;
  gap: 10px 18px;
}
.summary-line {
  font-size: 14px;
  line-height: 1.7;
  color: #707499;
}
.tag { background: rgba(122, 73, 226, 0.08); color: #6a43bf; border: 1px solid rgba(122, 73, 226, 0.16); border-radius: 20px; padding: 3px 10px; font-size: 0.82rem; }

.grid-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  width: 372px;
  margin: 0 auto;
  position: relative;
  background: transparent;
}
.grid-container::before,
.grid-container::after {
  content: "";
  position: absolute;
  pointer-events: none;
}
.grid-container::before {
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background-image: repeating-linear-gradient(
    to right,
    rgba(122, 73, 226, 0.24) 0 10px,
    transparent 10px 18px
  );
}
.grid-container::after {
  top: 0;
  left: 0;
  bottom: 0;
  width: 1px;
  background-image: repeating-linear-gradient(
    to bottom,
    rgba(122, 73, 226, 0.24) 0 10px,
    transparent 10px 18px
  );
}
.grid-cell {
  position: relative;
  padding: 8px 5px 6px;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  background: transparent;
  font-size: 15px;
  font-weight: 400;
  font-family: "SimSun", "宋体", serif;
  line-height: 1.45;
  aspect-ratio: 1/1;
  cursor: pointer;
  transition: background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.grid-cell::before,
.grid-cell::after {
  content: "";
  position: absolute;
  pointer-events: none;
}
.grid-cell::before {
  top: 0;
  right: 0;
  bottom: 0;
  width: 1px;
  background-image: repeating-linear-gradient(
    to bottom,
    rgba(122, 73, 226, 0.24) 0 10px,
    transparent 10px 18px
  );
}
.grid-cell::after {
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  background-image: repeating-linear-gradient(
    to right,
    rgba(122, 73, 226, 0.24) 0 10px,
    transparent 10px 18px
  );
}
.row-text {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 21px;
  font-size: 0.88rem;
  font-weight: 400;
}
.row-text:first-of-type {
  min-height: 18px;
  font-size: 0.88rem;
}
.row-text:nth-of-type(2) {
  min-height: 22px;
  font-size: 0.88rem;
  font-weight: 400;
}
.row-text:nth-of-type(3) {
  min-height: 21px;
  font-size: 0.88rem;
}
.row-text:last-of-type {
  min-height: 18px;
  font-size: 0.88rem;
  opacity: 0.92;
}
.ma-text {
  margin-left: 8px;
  font-size: 0.88rem;
  font-weight: 400;
}
.kong-mark {
  position: absolute;
  right: 4px;
  bottom: 4px;
  font-size: 12px;
  color: #0000ff;
  z-index: 10;
  line-height: 1;
  font-weight: 400;
}

.text-red { color: #ff0000; }
.text-black { color: #3f4063; }
.text-blue { color: #0000ff; }

.watermark { text-align: center; margin: 12px 0 5px; font-family: "楷体","KaiTi",serif; font-size: 1rem; letter-spacing: 4px; color: #8C2218; opacity: 0.35; user-select: none; }

.selected-cell {
  background: linear-gradient(135deg, rgba(122, 73, 226, 0.08) 0%, rgba(255, 255, 255, 0.72) 100%) !important;
  box-shadow:
    inset 0 0 0 1.5px rgba(122, 73, 226, 0.48),
    0 8px 18px rgba(122, 73, 226, 0.08);
  z-index: 5;
}
.analysis-board {
  margin-top: 22px;
  padding-top: 8px;
}
.analysis-board-head {
  text-align: center;
  margin-bottom: 16px;
}
.analysis-board-title {
  margin: 0;
  font-size: 1.18rem;
  color: #312f63;
}
.analysis-board-note {
  margin: 10px auto 0;
  max-width: 620px;
  font-size: 13px;
  line-height: 1.75;
  color: #707499;
}
.analysis-card-list {
  display: grid;
  gap: 14px;
}
.analysis-card {
  padding: 18px 18px 16px;
  border: 1px solid rgba(122, 73, 226, 0.12);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.68);
  box-shadow: 0 10px 22px rgba(82, 63, 141, 0.05);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}
.analysis-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 26px rgba(82, 63, 141, 0.08);
}
.analysis-card--selected {
  border-color: rgba(122, 73, 226, 0.28);
  background: linear-gradient(135deg, rgba(122, 73, 226, 0.05) 0%, rgba(157, 123, 240, 0.1) 100%);
  box-shadow: 0 16px 30px rgba(82, 63, 141, 0.1);
}
.analysis-card-top {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px 14px;
  margin-bottom: 12px;
}
.analysis-card-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.analysis-card-palace {
  font-size: 16px;
  font-weight: 700;
  color: #312f63;
}
.analysis-card-current {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(122, 73, 226, 0.12);
  color: #6a43bf;
  font-size: 12px;
  font-weight: 700;
}
.analysis-card-flags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.analysis-chip {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}
.analysis-chip--kong {
  background: rgba(91, 69, 181, 0.1);
  color: #5a45b5;
}
.analysis-chip--ma {
  background: rgba(32, 110, 218, 0.1);
  color: #1d62c4;
}
.analysis-card-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px dashed rgba(122, 73, 226, 0.16);
}
.analysis-kv {
  padding: 0;
  text-align: center;
}
.analysis-kv-label {
  display: block;
  margin-bottom: 4px;
  font-size: 11px;
  color: #8d90b0;
}
.analysis-kv-value {
  font-size: 14px;
  font-weight: 600;
  color: #312f63;
}
.analysis-card-body {
  display: grid;
  gap: 12px;
}
.analysis-item {
  margin-bottom: 0;
  padding: 0;
}
.analysis-item + .analysis-item {
  padding-top: 12px;
  border-top: 1px dashed rgba(122, 73, 226, 0.12);
}
.analysis-item strong {
  font-size: 1.05rem;
  color: #312f63;
}
.wx-tag {
  color: #666f95;
  margin-left: 5px;
  font-size: 0.9rem;
}
.detail-row {
  margin-top: 4px;
  margin-left: 10px;
  color: #333;
  font-size: 0.95rem;
}
.highlight {
  color: #ff0000;
  font-weight: bold;
}
.analysis-warning {
  display: inline-block;
  line-height: 1.65;
}

.site-footer {
  padding: 0 20px 20px;
}
.site-footer-inner {
  max-width: 940px;
  margin: 0 auto;
  text-align: center;
  color: #707499;
}
.footer-quote {
  font-size: 13px;
  letter-spacing: 0.16em;
  color: #312f63;
}
.footer-meta {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 1.8;
}
.footer-meta a {
  color: inherit;
}
.footer-meta a:hover {
  color: #7a49e2;
}
.site-records {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 16px;
}

@media (max-width: 480px) {
  .topbar {
    padding: 12px 12px 0;
  }
  .page-main {
    padding: 10px 12px 26px;
  }
  .container {
    padding: 14px 0 0;
  }
  .brand-name {
    font-size: 19px;
  }
  .brand-note {
    font-size: 13px;
  }
  .station-link {
    font-size: 16px;
  }
  .sub-tabs {
    width: 100%;
  }
  .site-footer {
    padding: 0 12px 18px;
  }
  .footer-quote {
    letter-spacing: 0.1em;
  }
  .analysis-board {
    padding-top: 4px;
  }
  .analysis-card {
    padding: 14px;
  }
  .analysis-card-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .grid-container {
    width: min(100%, 350px);
  }
  .grid-cell {
    font-size: 0.76rem;
    padding: 7px 4px 5px;
  }
  .row-text:first-of-type {
    font-size: 0.8rem;
  }
  .row-text:nth-of-type(2) {
    font-size: 0.8rem;
  }
  .row-text:nth-of-type(3) {
    font-size: 0.8rem;
  }
  .row-text:last-of-type {
    font-size: 0.8rem;
  }
  .cell-star, .cell-gate { font-size: 0.8rem; }
}
</style>
